import { AxiosError, isAxiosError } from 'axios';

type ServiceError = Error & {
    statusCode?: number;
    statusText?: string;
    errorCode?: string;
};

/**
 * Extracts error details from Axios errors or enhanced errors
 * and returns an enhanced error object to throw
 */
export const createServiceError = (error: unknown, context?: string): ServiceError => {
    // Handle Axios errors
    if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        const response = axiosError.response;

        if (response) {
            const { status, data, statusText } = response;

            // Extract specific message and error code from response data
            let errorMessage = axiosError.message;
            let errorCode: string | undefined;
            const responseData = data as any;

            if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                errorMessage = responseData.errors[0].message ?? errorMessage;
                errorCode = responseData.errors[0].code;
            } else if (responseData?.message) {
                errorMessage = responseData.message;
                errorCode = responseData.code;
            } else if (responseData?.error) {
                errorMessage = responseData.error;
            }

            // Create enhanced error
            const serviceError: ServiceError = new Error(context ? `${context}: ${errorMessage}` : errorMessage);
            serviceError.statusCode = status;
            serviceError.statusText = statusText;
            serviceError.errorCode = errorCode;

            return serviceError;
        }

        // Axios error without response - create enhanced error with context
        const serviceError: ServiceError = new Error(
            context ? `${context}: ${axiosError.message}` : axiosError.message
        );
        return serviceError;
    }

    // Handle already enhanced errors - return as is
    const possibleServiceError = error as any;
    if (possibleServiceError?.statusCode || possibleServiceError?.errorCode) {
        return error as ServiceError;
    }

    // Handle generic errors
    if (error instanceof Error) {
        const serviceError: ServiceError = new Error(context ? `${context}: ${error.message}` : error.message);
        return serviceError;
    }

    // Handle unknown error types
    return new Error(context ? `${context}: ${String(error)}` : String(error));
};
